import { gql, useQuery } from '@apollo/client';
import isEmpty from 'lodash/isEmpty';
import { useRiderStore } from '../../stores';

/* ============================================
   GRAPHQL QUERY DEFINITIONS
   ============================================ */

// Get all riders.
const GET_ALL_RIDERS = gql`
  query GetAllRiders {
    getAllRiders {
      id
      email
      name
      phoneNumber
      currentRideId
      completedRides
      homeAddress
      createdAt
      updatedAt
      FavoriteDrivers {
        id
        email
        name
        phoneNumber
        licensePlate
        carMake
        carModel
        carColor
        seats
        isOnDuty
        riderQueue
        driverNextAvailableAt
        profilePicture
        createdAt
        updatedAt
      }
    }
  }
`;

// Get single rider.
const GET_SINGLE_RIDER = gql`
  query GetRider($id: ID!) {
    getRider(id: $id) {
      id
      email
      name
      phoneNumber
      currentRideId
      completedRides
      homeAddress
      workAddress
      requestedRidesTotal
      acceptedRidesTotal
      canceledByRiderRidesTotal
      canceledByDriverRidesTotal
      completedRidesTotal
      expiredRidesTotal
      profilePicture
      createdAt
      updatedAt
      FavoriteDrivers {
        id
        email
        name
        phoneNumber
        licensePlate
        carMake
        carModel
        carColor
        seats
        isOnDuty
        riderQueue
        driverNextAvailableAt
        profilePicture
        createdAt
        updatedAt
      }
    }
  }
`;

// Get a rider's favorite drivers.
const GET_FAVORITE_DRIVERS = gql`
  query GetFavoriteDrivers($riderId: ID!) {
    getFavoriteDrivers(riderId: $riderId) {
      id
      email
      name
      phoneNumber
      licensePlate
      carMake
      carModel
      carColor
      seats
      isOnDuty
      riderQueue
      driverNextAvailableAt
      profilePicture
      createdAt
      updatedAt
    }
  }
`;

// Get a single favorite driver from rider's favorite drivers.
const GET_SINGLE_FAVORITE_DRIVER = gql`
  query GetSingleFavoriteDriver($riderId: ID!, $driverId: ID!) {
    getSingleFavoriteDriver(riderId: $riderId, driverId: $driverId) {
      id
      email
      name
      phoneNumber
      licensePlate
      carMake
      carModel
      carColor
      seats
      isOnDuty
      riderQueue
      driverNextAvailableAt
      profilePicture
      createdAt
      updatedAt
    }
  }
`;

// Get rider's completed rides.
const GET_RIDER_COMPLETED_RIDES = gql`
  query GetRiderCompletedRides($id: ID!) {
    getRiderCompletedRides(id: $id) {
      id
      riderId
      riderName
      phoneNumber
      passengers
      pickUp
      dropOff
      pickUpTime
      date
      bidAmount
      driverId
      driverName
      isRequested
      isAccepted
      isCanceled
      isCompleted
      isExpired
      canceledAt
      canceledBy
      completedAt
      expiredAt
      createdAt
      updatedAt
    }
  }
`;

// Get rider's ride history.
const GET_RIDER_RIDE_HISTORY = gql`
  query GetRiderRideHistory($id: ID!) {
    getRiderRideHistory(id: $id) {
      id
      riderId
      riderName
      phoneNumber
      passengers
      pickUp
      dropOff
      pickUpTime
      date
      bidAmount
      requestedDriverIds
      driverId
      driverName
      isRequested
      isAccepted
      isCanceled
      isCompleted
      isExpired
      canceledAt
      canceledBy
      completedAt
      expiredAt
      createdAt
      updatedAt
    }
  }
`;

/* ============================================
   QUERY HOOKS
   ============================================ */

// Get all riders
export const useAllRiders = () => {
  const {
    data: allRidersData,
    loading: loadingAllRiders,
    error: errorAllRiders,
    refetch: refetchAllRiders,
  } = useQuery(GET_ALL_RIDERS);

  return { allRidersData, loadingAllRiders, errorAllRiders, refetchAllRiders };
};

// Get a single rider
export const useSingleRider = (userId) => {
  const {
    data: riderData,
    loading: loadingRider,
    error: errorRider,
    refetch: refetchRider,
  } = useQuery(GET_SINGLE_RIDER, {
    variables: { id: userId },
    skip: isEmpty(userId),
  });

  return { riderData, loadingRider, errorRider, refetchRider };
};

// Get rider's favorite drivers
export const useRiderFavoriteDrivers = (userId) => {
  // Get function to store state change in rider store.
  const { setFavoriteDrivers } = useRiderStore();

  const {
    data: riderFavoriteDriversData,
    loading: loadingRiderFavoriteDrivers,
    error: errorRiderFavoriteDrivers,
    refetch: refetchRiderFavoriteDrivers,
  } = useQuery(GET_FAVORITE_DRIVERS, {
    variables: { riderId: userId },
    skip: isEmpty(userId),
    onCompleted: (data) => {
      // Once data is fetched successfully, update the store with the data.
      if (data && data.getFavoriteDrivers) {
        //TODO: Delete in production.
        // console.log(
        //   'Setting favorite drivers in store from query:',
        //   data.getFavoriteDrivers
        // );
        setFavoriteDrivers(data.getFavoriteDrivers);
      }
    },
  });

  return {
    riderFavoriteDriversData,
    loadingRiderFavoriteDrivers,
    errorRiderFavoriteDrivers,
    refetchRiderFavoriteDrivers,
  };
};

// Get a single rider's favorite driver
export const useRiderSingleFavoriteDriver = (userId, driverId) => {
  const {
    data: riderSingleFavoriteDriverData,
    loading: loadingRiderSingleFavoriteDriver,
    error: errorRiderSingleFavoriteDriver,
    refetch: refetchRiderSingleFavoriteDriver,
  } = useQuery(GET_SINGLE_FAVORITE_DRIVER, {
    variables: { riderId: userId, driverId },
    skip: isEmpty(userId),
  });

  return {
    riderSingleFavoriteDriverData,
    loadingRiderSingleFavoriteDriver,
    errorRiderSingleFavoriteDriver,
    refetchRiderSingleFavoriteDriver,
  };
};

// Get rider's completed rides
export const useRiderCompletedRides = (userId) => {
  const {
    data: completedRiderRidesData,
    loading: loadingCompletedRiderRides,
    error: errorCompletedRiderRides,
    refetch: refetchCompletedRiderRides,
  } = useQuery(GET_RIDER_COMPLETED_RIDES, {
    variables: { id: userId },
    skip: isEmpty(userId),
  });

  return {
    completedRiderRidesData,
    loadingCompletedRiderRides,
    errorCompletedRiderRides,
    refetchCompletedRiderRides,
  };
};

// Get rider's ride history
export const useRiderRideHistory = (userId) => {
  const {
    data: riderRideHistoryData,
    loading: loadingRiderRideHistory,
    error: errorRiderRideHistory,
    refetch: refetchRiderRideHistory,
  } = useQuery(GET_RIDER_RIDE_HISTORY, {
    variables: { id: userId },
    skip: isEmpty(userId),
  });

  return {
    riderRideHistoryData,
    loadingRiderRideHistory,
    errorRiderRideHistory,
    refetchRiderRideHistory,
  };
};
